import React, {useState, useRef, useCallback} from 'react';
import {View, StyleSheet, ScrollView, TouchableOpacity} from 'react-native';
import {Text} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, DrawerActions} from '@react-navigation/native';
import GradientHeader from '../../components/common/GradientHeader';
import WorkerListScreen from './WorkerListScreen';
import WorkerDuesScreen from './WorkerDuesScreen';
import WorkerPayoutsScreen from './WorkerPayoutsScreen';
import TaskAssignmentsScreen from './TaskAssignmentsScreen';
import DressAssignmentsScreen from './DressAssignmentsScreen';
import WorkerTypeListScreen from '../workertype/WorkerTypeListScreen';
import {colors, spacing} from '../../theme';

const TABS = [
  {key: 'list', component: WorkerListScreen},
  {key: 'dues', component: WorkerDuesScreen},
  {key: 'payouts', component: WorkerPayoutsScreen},
  {key: 'taskAssignments', component: TaskAssignmentsScreen},
  {key: 'dressAssignments', component: DressAssignmentsScreen},
  {key: 'workerTypes', component: WorkerTypeListScreen},
];

const TAB_LABEL_KEYS = {
  list: 'worker.tabList',
  dues: 'worker.tabDues',
  payouts: 'worker.tabPayouts',
  taskAssignments: 'worker.tabTaskAssignments',
  dressAssignments: 'worker.tabDressAssignments',
  workerTypes: 'worker.tabWorkerTypes',
};

const WorkersTabScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const [activeTab, setActiveTab] = useState('list');
  const scrollRef = useRef(null);
  const tabRefs = useRef({});

  const handleTabPress = useCallback((key) => {
    setActiveTab(key);
    // Scroll the pressed tab into view
    const ref = tabRefs.current[key];
    if (ref && scrollRef.current) {
      ref.measureLayout(
        scrollRef.current.getInnerViewRef(),
        (x) => {
          scrollRef.current.scrollTo({x: Math.max(0, x - 20), animated: true});
        },
        () => {},
      );
    }
  }, []);

  const ActiveComponent = TABS.find(tab => tab.key === activeTab)?.component;

  return (
    <View style={styles.container}>
      <GradientHeader
        title={t('worker.title')}
        onMenu={() => navigation.dispatch(DrawerActions.openDrawer())}
      />

      <View style={styles.tabBarContainer}>
        <ScrollView
          ref={scrollRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.tabBarContent}>
          {TABS.map(tab => {
            const isActive = activeTab === tab.key;
            return (
              <TouchableOpacity
                key={tab.key}
                ref={ref => { tabRefs.current[tab.key] = ref; }}
                onPress={() => handleTabPress(tab.key)}
                style={[styles.tab, isActive && styles.tabActive]}
                activeOpacity={0.7}>
                <Text
                  style={[
                    styles.tabLabel,
                    isActive ? styles.tabLabelActive : styles.tabLabelInactive,
                  ]}>
                  {t(TAB_LABEL_KEYS[tab.key])}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>

      <View style={styles.content}>
        {ActiveComponent && <ActiveComponent />}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  tabBarContainer: {
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  tabBarContent: {
    paddingHorizontal: spacing.xs,
  },
  tab: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    borderBottomWidth: 3,
    borderBottomColor: 'transparent',
  },
  tabActive: {
    borderBottomColor: colors.primary,
  },
  tabLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  tabLabelActive: {
    color: colors.primary,
  },
  tabLabelInactive: {
    color: colors.textSecondary,
  },
  content: {flex: 1},
});

export default WorkersTabScreen;
